/**
 * CR500 Phase 2B: SourceBlock
 * CR500 Phase 5B: V1 parity - validity formatting, "where found" display
 * 
 * Renders the source information in deal details.
 * V1 parity: Shows validity dates, source attribution, and links.
 */

import React from "react";
import { Clock, ExternalLink } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { formatValidity, formatWhereFound } from "@/lib/types/deals";
import { isV2DebugEnabled } from "@/lib/deal-cards/helpers/debugGuard";

/**
 * Extract domain from URL for display.
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export const SourceBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  const debugMode = isV2DebugEnabled();
  
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // V1 parity: Use same formatting as V1's SourceSection
  const validity = formatValidity(deal);
  const whereFound = formatWhereFound(deal);
  const sourceUrl = deal.source_url || deal.proof_urls?.[0] || null;
  const sourceName = deal.source_type || null;
  
  // If no source info at all, don't render (match V1)
  if (!validity && !whereFound && !sourceUrl && !sourceName) {
    if (debugMode) {
      return (
        <div 
          className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs"
          data-block="source"
          data-block-id={block.id}
        >
          <span className="font-medium text-yellow-600">[Debug] SourceBlock: No content</span>
          <div className="text-yellow-600/80 mt-1">
            Checked: validity, whereFound, source_url, source_type
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div 
      className="space-y-2"
      data-block="source"
      data-block-id={block.id}
    >
      <h4 className="text-sm font-semibold flex items-center gap-1.5">
        <Clock className="w-4 h-4" />
        {context.labels?.source_title || "Source & Validity"}
      </h4>
      
      <div className="space-y-1 text-sm text-muted-foreground">
        {/* Validity dates */}
        {validity && <p>{validity}</p>}
        
        {/* Where found attribution */}
        {whereFound && <p className="text-xs">{whereFound}</p>}
        
        {/* Source link */}
        {sourceUrl && (
          <a 
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            {sourceName || `View source (${extractDomain(sourceUrl)})`}
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      
      {/* Debug info */}
      {debugMode && (
        <div className="text-[10px] text-muted-foreground/50 border-t border-border/50 pt-1 mt-1">
          validity: {validity || 'null'} | whereFound: {whereFound || 'null'} | 
          source_type: {sourceName || 'null'} | source_url: {sourceUrl ? 'present' : 'null'}
        </div>
      )}
    </div>
  );
};

export default SourceBlock;
